import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dealsAPI, activitiesAPI } from '../utils/api';
import { ArrowLeft, Edit, Trash2, Plus, Calendar, DollarSign, TrendingUp, User } from 'lucide-react';
import DealModal from '../components/modals/DealModal';
import ActivityModal from '../components/modals/ActivityModal';

const DealDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);

  const fetchDeal = useCallback(async () => {
    try {
      const response = await dealsAPI.getById(id);
      setDeal(response.data);
    } catch (error) {
      console.error('Error fetching deal:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchActivities = useCallback(async () => {
    try {
      const response = await activitiesAPI.getAll({ deal_id: id });
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchDeal();
    fetchActivities();
  }, [fetchDeal, fetchActivities]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this deal?')) {
      try {
        await dealsAPI.delete(id);
        navigate('/deals');
      } catch (error) {
        alert('Failed to delete deal');
      }
    }
  };

  const formatCurrency = (value) => `RM ${(value / 1000).toFixed(0)}K`;
  const formatDate = (date) => date ? new Date(date).toLocaleDateString() : 'N/A';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Deal not found</p>
        <button onClick={() => navigate('/deals')} className="mt-4 text-primary">← Back to Deals</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/deals')} className="text-slate-600 hover:text-slate-900">
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{deal.project_title}</h1>
            <p className="text-slate-500 mt-1">{deal.deal_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
          >
            <Edit size={18} />
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
          >
            <Trash2 size={18} />
            Delete
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <DollarSign size={16} />
            <span className="text-sm">Total Value</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{formatCurrency(deal.total_value)}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <TrendingUp size={16} />
            <span className="text-sm">Probability</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{deal.probability}%</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <DollarSign size={16} />
            <span className="text-sm">Weighted Value</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{formatCurrency(deal.weighted_value || 0)}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <Calendar size={16} />
            <span className="text-sm">Year</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{deal.year}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="border-b border-slate-200">
          <div className="flex gap-6 px-6">
            {['overview', 'activities', 'history'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 text-sm font-medium capitalize border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Basic Information</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm text-slate-600">Client</dt>
                      <dd className="text-sm font-medium text-slate-900">{deal.client_id}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-slate-600">Channel</dt>
                      <dd className="text-sm font-medium text-slate-900">{deal.channel}</dd>
                    </div>
                    {deal.partner_id && (
                      <div>
                        <dt className="text-sm text-slate-600">Partner</dt>
                        <dd className="text-sm font-medium text-slate-900">{deal.partner_id}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-sm text-slate-600">Deal Type</dt>
                      <dd className="text-sm font-medium text-slate-900">{deal.deal_type}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-slate-600">Source</dt>
                      <dd className="text-sm font-medium text-slate-900">{deal.source}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-slate-600">Service Pillars</dt>
                      <dd className="flex flex-wrap gap-2 mt-1">
                        {deal.service_pillars?.map((p, i) => (
                          <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">{p}</span>
                        ))}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Pipeline Status</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm text-slate-600">Stage</dt>
                      <dd className="text-sm font-medium text-slate-900">{deal.stage}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-slate-600">Quarter</dt>
                      <dd className="text-sm font-medium text-slate-900">{deal.quarter || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-slate-600">BD Owner</dt>
                      <dd className="text-sm font-medium text-slate-900">{deal.bd_owner_id}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-slate-600">PO Status</dt>
                      <dd className="text-sm font-medium text-slate-900">{deal.po_status || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-slate-600">Created Date</dt>
                      <dd className="text-sm font-medium text-slate-900">{formatDate(deal.created_date)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-slate-600">Last Modified</dt>
                      <dd className="text-sm font-medium text-slate-900">{formatDate(deal.last_modified)}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              {deal.remarks && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Remarks</h3>
                  <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-lg">{deal.remarks}</p>
                </div>
              )}
            </div>
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Activities</h3>
                <button
                  onClick={() => setShowActivityModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  <Plus size={18} />
                  Log Activity
                </button>
              </div>
              <div className="space-y-4">
                {activities.length === 0 ? (
                  <p className="text-center py-8 text-slate-500">No activities logged yet</p>
                ) : (
                  activities.map((activity) => (
                    <div key={activity._id} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                              {activity.activity_type}
                            </span>
                            <span className="text-sm text-slate-500">{formatDate(activity.date)}</span>
                          </div>
                          <h4 className="font-medium text-slate-900 mb-1">{activity.subject}</h4>
                          {activity.notes && <p className="text-sm text-slate-600">{activity.notes}</p>}
                          {activity.outcome && (
                            <p className="text-sm text-slate-500 mt-2">Outcome: {activity.outcome}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Change History</h3>
              <p className="text-center py-8 text-slate-500">Audit history coming soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <DealModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          deal={deal}
          onSuccess={() => {
            fetchDeal();
            setShowEditModal(false);
          }}
        />
      )}

      {showActivityModal && (
        <ActivityModal
          isOpen={showActivityModal}
          onClose={() => setShowActivityModal(false)}
          preSelectedDealId={id}
          preSelectedClientId={deal.client_id}
          onSuccess={() => {
            fetchActivities();
            setShowActivityModal(false);
          }}
        />
      )}
    </div>
  );
};

export default DealDetail;